import React from 'react';
import { Phone } from 'lucide-react';

// ── REPLACE THESE WITH YOUR REAL NUMBERS ────────────────────────────────
// PHONE_DISPLAY = what the visitor sees.
// PHONE_TEL     = digits for the call link, E.164 format (no spaces).
// WHATSAPP_NUMBER = digits only, country code first, no "+" and no spaces.
// NOTA: se mantienen en 000 a propósito hasta que el sistema esté listo.
// Número real (Twilio) para activar después: +1 (239) 800-8804
export const PHONE_DISPLAY = '+1 (000) 000-0000';
export const PHONE_TEL = '+10000000000';
export const WHATSAPP_NUMBER = '10000000000';
// ──────────────────────────────────────────────────

const WHATSAPP_PREFILL = 'Hello! I would like help preparing my U.S. passport form.';
const WA_GREEN = '#25D366';
const telHref = `tel:${PHONE_TEL}`;
const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_PREFILL)}`;

// Official WhatsApp glyph (phone inside a speech bubble). Uses currentColor so
// it inherits text color (e.g. text-white). Decorative — the link itself
// carries the accessible name.
function WhatsAppIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" focusable="false" data-id="element-77" data-mp="2btp88upuv5rr">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.358.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.943 11.943 0 005.71 1.448h.005c6.582 0 11.946-5.358 11.949-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-78" data-mp="21xcejwub8rx0" />
    </svg>;
}

// Inline pair of buttons: "Call us" + "WhatsApp". Use inside sections.
export function ContactButtons({
  className = '',
  size = 'md',
  showNumber = false
}: {
  className?: string;
  size?: 'md' | 'lg';
  showNumber?: boolean;
}) {
  const pad = size === 'lg' ? 'px-7 py-4 text-lg' : 'px-5 py-3 text-base';
  return <div className={className} data-id="element-79" data-mp="39mf3o89ygee9">
      <div className="flex flex-col sm:flex-row gap-3" data-id="element-80" data-mp="2uriplefgswt9">
        <a href={telHref} className={`flex items-center justify-center gap-2 rounded-full font-semibold bg-brand-textPrimary text-white hover:opacity-90 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-textPrimary focus:ring-offset-2 ${pad}`} data-id="element-81" data-mp="2us0qwpgzi181">
          <Phone className="w-5 h-5" aria-hidden="true" data-id="element-82" data-mp="2c4rm2ws09e4f" />
          Call us
        </a>
        <a href={waHref} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-center gap-2 rounded-full font-semibold text-white hover:opacity-90 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${pad}`} style={{
        backgroundColor: WA_GREEN
      }} data-id="element-83" data-mp="2q3j06j6vazsk">
          <WhatsAppIcon className="w-5 h-5" data-id="element-84" data-mp="38sxyi5v65at9" />
          WhatsApp
        </a>
      </div>
      {showNumber && <p className="text-sm text-brand-textSecondary mt-3 text-center sm:text-left" data-id="element-85" data-mp="33tmfeq7bxt8i">
          Call or text: <span className="font-semibold" data-id="element-86" data-mp="3iqvgwcqckddy">{PHONE_DISPLAY}</span>
        </p>}
    </div>;
}

// Floating, always-visible Call + WhatsApp buttons (bottom-right on every page).
export function FloatingContact() {
  return <div className="fixed bottom-5 right-4 sm:right-5 z-[60] flex flex-col gap-3" data-id="element-87" data-mp="1b4vhlxf55ws4">
      <a aria-label="Chat with us on WhatsApp" href={waHref} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#25D366]" style={{
      backgroundColor: WA_GREEN
    }} data-id="element-88" data-mp="1g3okkvwsy5e1">
        <WhatsAppIcon className="w-7 h-7" data-id="element-89" data-mp="x52hxcln0680" />
      </a>
      <a aria-label="Call us" href={telHref} className="w-14 h-14 rounded-full bg-brand-textPrimary text-white shadow-lg flex items-center justify-center hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-textPrimary" data-id="element-90" data-mp="x7yht6h1p4z3">
        <Phone className="w-7 h-7" data-id="element-91" data-mp="2dm85dydgqpov" />
      </a>
    </div>;
}